import React, { useState } from 'react';
import MyAppText from '../../../components/MyAppText';
import { NativeSyntheticEvent, TextInputKeyPressEventData, View } from 'react-native';
import CustomButton from '../../../components/CustomButton';
import { useStyles } from 'react-native-unistyles';
import { translate } from '../../../services/translate';
import FilterInput from '../../../components/Filter/components/FilterInput';
import useFilters from '../../../components/Filter/hooks/useFilters';
import { DeacParamList } from '../../../typings/Params';
import { RouteProp, useIsFocused, useRoute } from '@react-navigation/native';
import { Pagination } from '../../../components/Pagination';
import MyDataTable from '../../../components/MyDataTable';
import DropdownButton, { Action } from '../../../components/DropdownButton';
import { deacService } from '../../../services/central-api/deac';
import Badge from '../../../components/Badge';
import { DEFAULT_PASTEL_COLORS, DEFAULT_PASTEL_TEXT_COLORS } from '../../../styles/colorPalettes';
import { getVacancyStatus, useVacancyStatus } from './VacanciesCommons';
import moment from 'moment';
import DeleteVacancyModal from './DeleteVacancyModal';
import Filter from './Filters';
import Tooltip from '../../../components/Tooltip';
import { useAuth } from '../../../context/Auth';
import { getDeacSchedule } from '../DeacCommons';
import { ClientError } from '../../../services/central-api/base-service';
import Toast from 'react-native-toast-message';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons';
import CloseApplicationsModal from './CloseApplicationsModal';
import ExportAgentsSchedulesConfirmationModal from './ExportAgentsSchedulesConfirmationModal';
import ExportAgentsSchedulesSelectModal from './ExportAgentsSchedulesSelectModal';
import DeleteMultipleVacanciesModal from './DeleteMultipleVacanciesModal';
import { useQuery } from '@tanstack/react-query';
import Container from '../../../components/Container';
import Header from '../../../components/Header';
import Card from '../../../components/Card';
import DataTableHeader from '../../../components/DataTableHeader';
import DataTableContainer from '../../../components/DataTableContainer';
import { RootNavigation } from '../../../Routes';

export default function Vacancies({ navigation }: { navigation: RootNavigation; }) {
    const { theme } = useStyles();
    const route = useRoute<RouteProp<DeacParamList, 'Vacancies'>>();
    const { user: authenticatedUser } = useAuth();

    const InViewMode = !(authenticatedUser?.permissions.deac_manage_vacancies ||
        authenticatedUser?.permissions.deac_manage_acting_body_units_vacancies ||
        authenticatedUser?.permissions.deac_manage_all_acting_bodies_vacancies
    );

    const [filters, setFilters] = useFilters<DeacParamList['Vacancies']>({
        ...route.params,
        page: Math.max(+route.params.page - 1, 0),
        limit: Number(route.params.limit) || 25,
    });
    const [textFilter, setTextFilter] = useState(filters.textFilter ?? '');
    const [selectedVacancy, setSelectedVacancy] = useState<Vacancy | undefined>(undefined);
    const [isDeleteModalVisible, setIsDeleteModalVisible] = useState<boolean>(false);
    const [isLoadingActions, setIsLoadingActions] = useState<boolean>(false);
    const [closeApplicationsConfirmModal, setCloseApplicationsConfirmModal] = useState<boolean>(false);
    const [agentScheduleFileFormat, setAgentScheduleFileFormat] = useState<'pdf' | 'csv'>('pdf');
    const [vacanciesClosingApplicationsIds, setVacanciesClosingApplicationsIds] = useState<number[]>([]);
    const [selectedVacancies, setSelectedVacancies] = useState<Vacancy[]>([]);
    const [clearSelectedRows, setClearSelectedRows] = useState<boolean>(false);
    const [currentAction, setCurrentAction] = useState<'none' | 'closeApplications' | 'exportSchedule' | 'cancelVacancies'>('none');

    const [exportMultipleScheduleConfirmModal, setExportMultipleScheduleConfirmModal] = useState<boolean>(false);
    const [exportScheduleModal, setExportScheduleModal] = useState<boolean>(false);
    const [scheduleFileFormat, setScheduleFileFormat] = useState<'pdf' | 'csv' | 'xlsx' | ''>('');

    const [cancelVacanciesConfirmModal, setCancelVacanciesConfirmModal] = useState<boolean>(false);
    const [tagsTooltipHeight, setTagsTooltipHeight] = useState<{ [key: number]: number; }>({});

    const isFocused = useIsFocused();
    const queryResult = useQuery({
        queryKey: ['deacVacancies', filters],
        queryFn: () => deacService.getVacancies(filters),
        enabled: !!authenticatedUser && isFocused,
    });

    const isEnabled = !!authenticatedUser;
    const isLoading = isEnabled && queryResult.isFetching;

    const { data, refetch } = queryResult;

    function handleKeyDown(e: NativeSyntheticEvent<TextInputKeyPressEventData>) {
        if (e.nativeEvent.key == 'Enter') {
            navigation.setParams({
                ...filters,
                textFilter: textFilter !== '' ? textFilter : undefined,
                page: 1,
            });
            setFilters(old => ({
                ...old,
                textFilter: textFilter !== '' ? textFilter : undefined,
                page: 0,
            }));
        }
    }

    function getOpeningDate(vacancy: Vacancy) {
        if (moment().isSame(vacancy.applicationsStart, 'day')) {
            return translate('today');
        } else if (moment().add(1, 'day').isSame(vacancy.applicationsStart, 'day')) {
            return translate('tomorrow');
        }
        return moment(vacancy.applicationsStart).format('DD/MM');
    }

    function getTagTextColor(color: string) {
        const index = DEFAULT_PASTEL_COLORS.indexOf(color);
        return DEFAULT_PASTEL_TEXT_COLORS[index];
    }

    async function getSchedule(mode: 'pdf' | 'csv' | 'xlsx', ids?: number[]) {
        await getDeacSchedule(mode, setIsLoadingActions, filters, ids);
        setCurrentAction('none');
    }

    async function closeApplications() {
        const idsToClose = selectedVacancies.map(v => v.id);
        try {
            setIsLoadingActions(true);
            setVacanciesClosingApplicationsIds([
                ...vacanciesClosingApplicationsIds,
                ...idsToClose
            ]);

            const applicationsFinish = moment().toDate();
            await deacService.closeVacanciesApplications({
                vacanciesIds: idsToClose,
                applicationsFinish
            });

            if (data && data.rows) {
                data.rows = data?.rows.map(v => {
                    if (idsToClose.includes(v.id)) {
                        return {
                            ...v,
                            applicationsFinish: applicationsFinish
                        };
                    }
                    return v;
                });
            }
        } catch (err) {
            if (err instanceof ClientError) {
                return Toast.show({
                    type: 'sentinelxError',
                    text1: translate(err.message),
                });
            }

            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        } finally {
            setVacanciesClosingApplicationsIds(vacanciesClosingApplicationsIds.filter(id => !idsToClose.includes(id)));
            setSelectedVacancies([]);
            setClearSelectedRows(false);
            setIsLoadingActions(false);
            setCurrentAction('none');
            // getVacancies();
            refetch();
        }
    }

    function getActions() {
        const actions: Action[] = [];

        actions.push({
            content: translate('exportSchedule'),
            onPress: () => {
                setClearSelectedRows(false);
                setCurrentAction('exportSchedule');
            }
        });

        if (!InViewMode) {
            actions.push({
                content: translate('closeApplications'),
                variant: 'danger',
                onPress: () => {
                    setClearSelectedRows(false);
                    setCurrentAction('closeApplications');
                }
            });


            actions.push({
                content: translate('cancelVacancies'),
                variant: 'danger',
                onPress: () => {
                    setClearSelectedRows(false);
                    setCurrentAction('cancelVacancies');
                }
            });
        }

        return actions;
    }

    function getSelectionTooltip() {
        if (currentAction === 'closeApplications') {
            return translate('actionAvailableOnlyToOpenVacancies');
        } else if (currentAction === 'exportSchedule') {
            return translate('actionAvailableOnlyToOpenConfirmedProgressVacancies');
        } else if (currentAction === 'cancelVacancies') {
            return translate('actionAvailableOnlyToScheduledOpenVacancies');
        } else {
            return translate('disabled');
        }
    }

    function onLayoutTooltipContent(vacancyId: number, event: { nativeEvent: { layout: { x: number; y: number; width: number; height: number; }; }; }) {
        setTagsTooltipHeight(prev => ({
            ...prev,
            [vacancyId]: event.nativeEvent.layout.height
        }));
    }

    return (
        <Container>
            <Header
                title={translate('vacanciesManagement')}
                actions={<CustomButton text={translate('registerVacancy')} onPress={() => {
                    setSelectedVacancy(undefined);
                    navigation.navigate('Deac', { screen: 'VacancyForm' });
                }} />}
            />
            <Card>
                <DataTableHeader
                    title={translate('registeredVacancies')}
                    zIndex={2}
                    filterInput={<FilterInput
                        value={textFilter}
                        placeholder={translate('typeToSearch')}
                        onChange={setTextFilter}
                        onKeyPress={handleKeyDown}
                    />}
                    filter={<View style={{ flexDirection: 'row', gap: 10 }}>
                        <Filter
                            filters={filters}
                            setFilters={setFilters}
                            textFilter={textFilter}
                            setTextFilter={setTextFilter}
                        />
                        {currentAction === 'none'
                            ? <DropdownButton
                                viewStyle={{ height: '100%' }}
                                isLoading={isLoadingActions}
                                isButton
                                actions={getActions()}
                                buttonIcon={faChevronDown}
                                buttonType='primary'
                                buttonLabel='actions'
                                offsetX={39}
                                offsetY={45}
                                size={164}
                            />
                            : <>
                                <CustomButton
                                    text={translate('cancelSelection')}
                                    type='secondary'
                                    disabled={isLoadingActions}
                                    onPress={() => {
                                        setClearSelectedRows(true);
                                        setSelectedVacancies([]);
                                        setCurrentAction('none');
                                    }}
                                />

                                {currentAction === 'exportSchedule'
                                    ? <DropdownButton
                                        viewStyle={{ height: '100%' }}
                                        isLoading={isLoadingActions}
                                        disabled={selectedVacancies.length === 0}
                                        isButton
                                        actions={[
                                            {
                                                content: translate('exportScheduleAsPdf'),
                                                onPress: () => {
                                                    setAgentScheduleFileFormat('pdf');
                                                    setExportMultipleScheduleConfirmModal(true);
                                                }
                                            },
                                            {
                                                content: translate('exportScheduleAsCsv'),
                                                onPress: () => {
                                                    setAgentScheduleFileFormat('csv');
                                                    setExportMultipleScheduleConfirmModal(true);
                                                }
                                            }
                                        ]}
                                        buttonIcon={false}
                                        buttonType='primary'
                                        buttonLabel='exportSchedule'
                                        offsetX={39}
                                        offsetY={45}
                                        size={164}
                                    />
                                    : <CustomButton
                                        text={translate('concludeSelection')}
                                        type='primary'
                                        disabled={selectedVacancies.length === 0}
                                        isLoading={isLoadingActions}
                                        onPress={() => {
                                            if (currentAction === 'closeApplications') {
                                                setCloseApplicationsConfirmModal(true);
                                            } else if (currentAction === 'cancelVacancies') {
                                                setCancelVacanciesConfirmModal(true);
                                            }
                                        }}
                                    />
                                }
                            </>
                        }
                    </View>}
                />
                <DataTableContainer
                    dataTable={<MyDataTable
                        clearSelectedRows={clearSelectedRows}
                        selectableRows={!InViewMode && currentAction !== 'none'}
                        disableSelectTooltip={getSelectionTooltip()}
                        selectableRowDisabled={(row) => {
                            if (currentAction === 'closeApplications') {
                                const vacancyStatus = useVacancyStatus(row);
                                return !(vacancyStatus.isOpened);
                            } else if (currentAction === 'exportSchedule') {
                                const vacancyStatus = useVacancyStatus(row);
                                return !(vacancyStatus.isOpened || vacancyStatus.isConfirmed || vacancyStatus.isProgress);
                            } else if (currentAction === 'cancelVacancies') {
                                const vacancyStatus = useVacancyStatus(row);
                                return !(vacancyStatus.isScheduled || vacancyStatus.isOpened);
                            } else {
                                return true;
                            }
                        }}
                        onSelectedRowsChange={(selectedRows) => {
                            setSelectedVacancies(selectedRows.selectedRows);
                        }}
                        columns={[
                            {
                                name: translate('status'),
                                cell: row => getVacancyStatus({ vacancy: row }),
                                grow: 0,
                            },
                            {
                                name: translate('id'),
                                cell: row => row.id,
                                minWidth: '75px',
                                grow: 0,
                            },
                            {
                                name: translate('activity'),
                                cell: row => row.title,
                                grow: 1,
                            },
                            {
                                name: translate('actingBody'),
                                cell: row => row.workplace.actingBodyUnit.actingBody.name,
                                grow: 1,
                            },
                            {
                                name: translate('command'),
                                cell: row => row.workplace.actingBodyCommand.name,
                                grow: 1,
                            },
                            {
                                name: translate('unit'),
                                cell: row => row.workplace.actingBodyUnit.name,
                                grow: 1,
                            },
                            {
                                name: translate('level'),
                                selector: row => row.levels.map(l => l.name).join(', '),
                                wrap: true,
                                grow: 1,
                            },
                            {
                                name: translate('opening'),
                                selector: row => getOpeningDate(row),
                                wrap: true,
                                grow: 0,
                            },
                            {
                                name: translate('activityDate'),
                                selector: row => moment(row.activityStart).format('DD/MM'),
                                wrap: true,
                                grow: 0,
                            },
                            {
                                name: translate('createdBy'),
                                selector: row => row.user.warName && row.user.warName != '' ? row.user.warName : row.user.name,
                                wrap: true,
                                grow: 1,
                            },
                            {
                                name: translate('tags'),
                                cell: (row, rowIndex) =>
                                    row.tags && row.tags.length > 0 ? <Tooltip
                                        useSimpleBox
                                        style={{
                                            color: theme.colors.white['500'],
                                            padding: 16,
                                            borderRadius: 12,
                                            justifyContent: 'center',
                                            zIndex: 9999,
                                            width: row.tags.length >= 6 ? 450 : 150,
                                            background: theme.colors.neutral['500'],
                                            top: rowIndex > (filters.limit - 5) ? -tagsTooltipHeight[row.id] - 12 : 0
                                        }}
                                        tooltipContent={<View onLayout={(event) => onLayoutTooltipContent(row.id, event)} style={{ flexDirection: 'row', flexWrap: 'wrap', width: '100%', gap: 5 }}>
                                            {row.tags?.map(tag => (
                                                <Badge mode='ROUNDED' key={tag.id} backgroundColor={tag.color} size='small' textColor={getTagTextColor(tag.color)}>{tag.name}</Badge>
                                            ))}
                                        </View>}
                                        tooltipDirection='left'
                                    >
                                        <MyAppText
                                            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                            // @ts-ignore
                                            style={{ cursor: 'pointer', fontWeight: '600', fontSize: 14 }}
                                        >
                                            {translate('viewTags').toLowerCase()}
                                        </MyAppText>
                                    </Tooltip> : null,
                                wrap: true,
                                grow: 0,
                            },
                            {
                                name: '',
                                cell: row => {
                                    const isScheduled = (row.deletedAt == null && moment().isBefore(moment(row.applicationsStart)));
                                    const isOpened = row.applicationsFinish
                                        ? (row.deletedAt == null && moment().isAfter(moment(row.applicationsStart)) && moment().isBefore(moment(row.applicationsFinish)))
                                        : (row.deletedAt == null && moment().isAfter(moment(row.applicationsStart)) && moment().isBefore(moment(row.activityStart)));
                                    const isConfirmed = row.applicationsFinish
                                        ? (row.deletedAt == null && moment().isAfter(moment(row.applicationsFinish)) && moment().isBefore(moment(row.activityStart)))
                                        : false;
                                    const isProgress = (row.deletedAt == null && moment().isAfter(moment(row.activityStart)) && moment().isBefore(moment(row.activityFinish)));
                                    const isFinished = (row.deletedAt == null && moment().isAfter(moment(row.activityFinish)));
                                    const isCanceled = (row.deletedAt != null);
                                    const isViewMode = (InViewMode || isProgress || isFinished || isCanceled);

                                    const actions: Action[] = [];

                                    if (isOpened || isConfirmed || isProgress) {
                                        actions.push({
                                            content: translate('exportSchedule'),
                                            onPress: () => {
                                                setSelectedVacancies([row]);
                                                setExportScheduleModal(true);
                                            }
                                        });
                                    }

                                    actions.push({
                                        content: isViewMode ? translate('viewInformation') : translate('editInformation'),
                                        onPress: () => {
                                            setSelectedVacancy(row);
                                            navigation.navigate('Deac', { screen: 'VacancyForm', params: { id: row.id } });
                                        }
                                    });

                                    if (isOpened && !isViewMode) {
                                        actions.push({
                                            content: translate('closeApplications'),
                                            variant: 'danger',
                                            onPress: () => {
                                                setSelectedVacancies([row]);
                                                setCloseApplicationsConfirmModal(true);
                                            }
                                        });
                                    }

                                    if ((isOpened || isScheduled) && !isViewMode) {
                                        actions.push({
                                            content: translate('cancelVacancy'),
                                            variant: 'danger',
                                            onPress: () => {
                                                setSelectedVacancy(row);
                                                setIsDeleteModalVisible(true);
                                            },
                                        });
                                    }
                                    return (
                                        <DropdownButton actions={actions} isLoading={vacanciesClosingApplicationsIds.includes(row.id)} />
                                    );
                                },
                                wrap: true,
                                button: true
                            }
                        ]}
                        data={data?.rows || []}
                        progressPending={isLoading}
                    />}
                    pagination={<Pagination
                        currentPage={filters.page}
                        totalItems={data?.count || 0}
                        setPage={page => {
                            setFilters(old => ({ ...old, page }));
                            navigation.setParams({ ...route.params, page: page + 1 });
                        }}
                        pageSize={filters.limit}
                        setPageSize={pageSize => {
                            setFilters(old => ({ ...old, limit: pageSize, page: 0 }));
                            navigation.setParams({ ...route.params, limit: pageSize, page: 1 });
                        }}
                    />}
                />
            </Card>
            {selectedVacancies.length > 0 && <CloseApplicationsModal
                isModalVisible={closeApplicationsConfirmModal}
                setIsModalVisible={setCloseApplicationsConfirmModal}
                onConfirm={() => {
                    setClearSelectedRows(true);
                    setCloseApplicationsConfirmModal(false);
                    closeApplications();
                }}
                selectedVacancies={selectedVacancies}
            />
            }

            {selectedVacancies.length > 0 && <DeleteMultipleVacanciesModal
                isModalVisible={cancelVacanciesConfirmModal}
                setModalVisible={setCancelVacanciesConfirmModal}
                onConfirm={() => {
                    setClearSelectedRows(true);
                    setCancelVacanciesConfirmModal(false);
                    setCurrentAction('none');
                    refetch();
                }}
                selectedVacancies={selectedVacancies}
            />
            }

            {selectedVacancies.length > 0 && <ExportAgentsSchedulesConfirmationModal
                isModalVisible={exportMultipleScheduleConfirmModal}
                setIsModalVisible={setExportMultipleScheduleConfirmModal}
                fileFormat={translate(agentScheduleFileFormat.toUpperCase())}
                onConfirm={() => {
                    setClearSelectedRows(true);
                    setExportMultipleScheduleConfirmModal(false);
                    getSchedule(agentScheduleFileFormat, selectedVacancies.map(v => v.id));
                }}
                selectedVacancies={selectedVacancies}
            />
            }
            {selectedVacancies.length > 0 && <ExportAgentsSchedulesSelectModal
                isModalVisible={exportScheduleModal}
                setIsModalVisible={setExportScheduleModal}
                onConfirm={() => {
                    setExportScheduleModal(false);
                    if (scheduleFileFormat != '') {
                        getSchedule(scheduleFileFormat, selectedVacancies.map(v => v.id));
                    }
                    setScheduleFileFormat('');
                }}
                fileExtension={scheduleFileFormat}
                setFileExtension={setScheduleFileFormat}
            />}
            {selectedVacancy && <DeleteVacancyModal
                onConfirm={refetch}
                vacancy={selectedVacancy}
                isModalVisible={isDeleteModalVisible}
                setModalVisible={setIsDeleteModalVisible}
            />}
        </Container>
    );
}
